import { useState, useEffect, useCallback } from 'react';

import { fetchPosts, fetchPublishedBlogs } from '../redux/blog/blogSlice'; // add import
import { AppDispatch, RootState } from '../redux';
import { useDispatch, useSelector } from 'react-redux';
import api from '../redux/api'

export interface BlogPost {
  id: string;
  _id?: string;
  id_practice?: string;
  title: string;
  description?: string;
  excerpt?: string;
  slug: string;
  content: string;
  author: string;
  coverImage?: string;
  readTime?: number;
  tags?: string[];
  tag?: string[];
  status: 'draft' | 'pending_review' | 'published' | 'rejected';
  published_at?: number | null;
  created_at: number | null;
  createdAt?: string | null;
  publishedAt?: string | null;
  review_results?: string | null;
  rejection_reason?: string | null;
}

export function useBlogPosts(options?: { publishedOnly?: boolean; query?: string }) {
  const dispatch = useDispatch<AppDispatch>();
  const posts = useSelector((state: RootState) => state.blog.posts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      if (options?.publishedOnly) {
        await dispatch(fetchPublishedBlogs()).unwrap();
      } else {
        await dispatch(fetchPosts(options?.query)).unwrap();
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  }, [dispatch, options?.publishedOnly, options?.query]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const token = JSON.parse(localStorage.getItem('therapist_direct_session') || '{}').token;

  const addPost = async (post: Partial<Omit<BlogPost, 'id' | 'createdAt'>> & { id_practice: string }) => {
    
    try {
      const response = await api.post('/blog', post, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      

      const newPost = await response.data;
      dispatch(fetchPosts()); // Fetch posts again to update the state
      return newPost.body;

    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to add post');
    }
  };

  const updatePost = async (id: string, updates: Partial<BlogPost>) => {
    try {
      // Get the current post from the store
      const currentPost = posts.find((post: BlogPost) => post.id === id);
      if (!currentPost) {
        throw new Error('Post not found');
      }

      // Merge the updates with the current post data to ensure we don't lose any fields
      const updatedData: BlogPost = {
        ...currentPost,
        ...updates,
        // Ensure these fields are preserved
        id: currentPost.id,
        slug: currentPost.slug,
        createdAt: currentPost.createdAt,
        // Only update publishedAt if status is changing to published
        publishedAt: updates.status === 'published' ? new Date().toISOString() : currentPost.publishedAt
      };

      const response = await api.put(`/blog/${id}`, updatedData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.data) {
        throw new Error('Failed to update post');
      }

      // Refresh the posts list
      await dispatch(fetchPosts());
      return true;
    } catch (err) {
      console.error('Update post error:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to update post');
    }
  };

  const updatePostStatus = async (id: string, updates: Partial<BlogPost>) => {
    try {

      const response = await api.put(`/custodian/blog/${id}`, updates, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      // if (!response.data.ok) {
      //   throw new Error('Failed to update post');
      // }
      const updatedPost = await response.data;
      dispatch(fetchPosts()); // Fetch posts again to update the state
      return updatedPost;
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to update post');
    }
  };

  return {
    posts,
    loading,
    error,
    addPost,
    updatePost,
    updatePostStatus
  };
}
